import React, { useState } from 'react';
import axiosInstance from '../../../utils/axios';
import toast from 'react-hot-toast';
import CheckManagementForm from './CheckManagementForm';

// ============================================
// Component: PaymentCollectionModal (Admin Version)
// Purpose: Admin can record payment for any company
// Enhanced Features:
// - Company selection dropdown
// - Auto-approval (admin payments don't need approval)
// - Document upload support
// ============================================

const PaymentCollectionModal = ({ isOpen, onClose, preselectedCompany = null, onSuccess }) => {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [formData, setFormData] = useState({
    company_id: preselectedCompany?.id || '',
    amount: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    check_number: '',
    bank_number: '',
    branch_number: '',
    bank_reference: '',
    notes: '',
    checks: [],
    cash_discount: false
  });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isOpen && !preselectedCompany) {
      fetchCompanies();
    }
  }, [isOpen, preselectedCompany]);

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axiosInstance.get('/api/admin/payments/companies-overview');
      setCompanies(response.data.companies);
    } catch (error) {
      toast.error('שגיאה בטעינת רשימת חברות');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const selectedCompanyData = preselectedCompany || companies.find(c => c.company_id === formData.company_id);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const amount = parseFloat(formData.amount);

    if (!formData.company_id) {
      toast.error('נא לבחור חברה');
      return;
    }

    if (!formData.amount || amount <= 0) {
      toast.error('נא להזין סכום תקין');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post('/api/admin/payments', {
        ...formData,
        amount: amount
      });

      toast.success('התשלום נרשם בהצלחה!');
      onSuccess && onSuccess(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה ברישום התשלום');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" dir="rtl">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black opacity-50" onClick={onClose}></div>

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">רישום גבייה</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Company Selection (if not preselected) */}
              {!preselectedCompany && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    בחר חברה *
                  </label>
                  {loadingCompanies ? (
                    <div className="text-sm text-gray-500">טוען חברות...</div>
                  ) : (
                    <select
                      value={formData.company_id}
                      onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    >
                      <option value="">-- בחר חברה --</option>
                      {companies.map(company => (
                        <option key={company.company_id} value={company.company_id}>
                          {company.company_name} (חוב: ₪{company.current_balance.toFixed(2)})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Company Info Card */}
              {selectedCompanyData && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-gray-600">חברה:</div>
                  <div className="text-lg font-bold text-gray-900">
                    {preselectedCompany?.name || selectedCompanyData.company_name}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">חוב נוכחי: </span>
                      <span className="font-bold text-red-600">
                        ₪{(preselectedCompany?.current_balance || selectedCompanyData.current_balance).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">אשראי זמין: </span>
                      <span className="font-bold text-green-600">
                        ₪{(preselectedCompany?.available_credit || selectedCompanyData.available_credit).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  סכום גבייה *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  אמצעי תשלום *
                </label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value, cash_discount: false })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="cash">מזומן</option>
                  <option value="check">צ'ק</option>
                  <option value="bank_transfer">העברה בנקאית</option>
                </select>
              </div>

              {/* Cash Discount Option (only for cash payments) */}
              {formData.payment_method === 'cash' && (
                <div className="flex items-start space-x-2 space-x-reverse p-3 bg-green-50 border border-green-200 rounded-md">
                  <input
                    type="checkbox"
                    id="cash_discount"
                    checked={formData.cash_discount}
                    onChange={(e) => setFormData({ ...formData, cash_discount: e.target.checked })}
                    className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="cash_discount" className="flex-1 text-sm">
                    <div className="font-medium text-gray-900">
                      הנחת מזומן (18%)
                    </div>
                    <div className="text-gray-600 text-xs mt-1">
                      {formData.cash_discount && formData.amount ? (
                        <>
                          <div className="font-semibold text-green-700">
                            סכום בפועל: ₪{parseFloat(formData.amount).toFixed(2)}
                          </div>
                          <div className="font-semibold text-green-700">
                            יקוזז מהחוב: ₪{(parseFloat(formData.amount) * 1.18).toFixed(2)}
                          </div>
                          <div className="text-gray-500 mt-1">
                            (הנחה של 18% על מע"מ בגין תשלום במזומן)
                          </div>
                        </>
                      ) : (
                        'מאפשר לקזז מהחוב סכום גבוה ב-18% מהסכום ששולם בפועל'
                      )}
                    </div>
                  </label>
                </div>
              )}

              {formData.payment_method === 'check' && (
                <CheckManagementForm formData={formData} setFormData={setFormData} />
              )}

              {formData.payment_method === 'bank_transfer' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    אסמכתא בנקאית
                  </label>
                  <input
                    type="text"
                    value={formData.bank_reference}
                    onChange={(e) => setFormData({ ...formData, bank_reference: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="מספר אסמכתא"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  תאריך תשלום
                </label>
                <input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  הערות
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="הערות נוספות..."
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {loading ? 'שומר...' : 'רשום גבייה'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 font-medium"
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentCollectionModal;
